import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useServer } from "./ServerContext";
import { cashEndpoints, checkoutEndpoints, deliveryEndpoints, eatInEndpoints, pickupEndponts, stripeSettingsEndpoints } from "../env";
import { DeliveryMethod } from "../Enum/DeliveryMethod";
import { loadStripe } from "@stripe/stripe-js";
import { useCart } from "./CartContext";
import { PaymentMethod } from "../Enum/PaymentMethod";
import { type } from "@testing-library/user-event/dist/type";
import { OrderOnlineErrorsMsg } from "../Enum/OrderOnlineErrors";
import { useBus } from "react-bus";
import { useNavigate } from "react-router-dom";

const CheckoutContext = createContext(undefined);

export const CheckoutProvider = ({children}) => {
    //HOOKS
    const { getRequest, postRequest } = useServer();
    const { getCartToken } = useCart();
    const bus = useBus();
    const navigate = useNavigate();

    //REFS
    const stripePromise = useRef(null);
    const stripeOptions = useRef(null);

    //STATES
    const [pickupSettings, setPickupSettings] = useState(null);
    const [eatInSettings, setEatInSettings] = useState(null);
    const [deliverySettings, setDeliverySettings] = useState(null);
    const [deliveryAreas, setDeliveryAreas] = useState([]);
    const [checkoutStep, setCheckoutStep] = useState(0);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState("");

    //DELIVERY
    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(DeliveryMethod.DELIVERY);
    const [address, setAddress] = useState("");
    const [apartment, setApartment] = useState("");
    const [floor, setFloor] = useState("");
    const [entrance, setEntrance] = useState("");
    const [deliveryPrice, setDeliveryPrice] = useState(0);
    const [postCode, setPostCode] = useState(0);
    const [customerLatitude, setCustomerLatitude] = useState(0);
    const [customerLongitude, setCustomerLongitude] = useState(0);

    const [latitude, setLatitude] = useState(0);
    const [longitude, setLongitude] = useState(0);
    //CONTACT
    const [name, setName] = useState("");
    const [lastname, setLastname] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");

    //PAYMENT
    const [paymentMethod, setPaymentMethod] = useState(PaymentMethod.STRIPE);
    const [stripeSettings, setStripeSettings] = useState(null);
    const [cashSettings, setCashSettings] = useState(null);
    const [tip, setTip] = useState(0);

    //STRIPE
    const [stripeClientSecret, setStripeClientSecret] = useState("");

    //FUNCTIONS
    const loadStripeSettings = () => {
        getRequest(stripeSettingsEndpoints.get_stripe, {}, (res) => {
            if (res[0])
            {
                setStripeSettings(res[1].stripe);
            }
        });
    }

    const loadPickupSettings = () => {
        getRequest(pickupEndponts.get_pickup, {}, (res) => {
            if (res[0])
            {
                setPickupSettings(res[1].pickup);
            }
        });
    }

    const loadEatInSettings = () => {
        getRequest(eatInEndpoints.get, {}, ([status, res]) => {
            if (status)
            {
                setEatInSettings(res.settings);
            }
        });
    }

    const loadDeliverySettings = () => {
        getRequest(deliveryEndpoints.get_delivery, {}, (res) => {
            if (res[0])
            {
                const sortedDeliveryAreas = res[1].delivery.delivery_areas.sort((a, b) => a.area - b.area);
                setDeliverySettings(res[1].delivery);
                setDeliveryAreas(sortedDeliveryAreas);
            }
        });
    }

    const loadCashSettings = () => {
        getRequest(cashEndpoints.get_cash_settings, {}, (res) => {
            if (res[0])
            {
                setCashSettings(res[1].cash_settings);
            }
        });
    }

    const postCheckout = () => {
        if (checkoutLoading)
        {
            return;
        }

        setCheckoutLoading(true);
        setCheckoutError("");

        const data = getCheckoutData();

        postRequest(checkoutEndpoints.post_order, data, (res) => {
            setCheckoutLoading(false);

            if (res[0])
            {
                res = res[1];
                if (res.success)
                {
                    
                    if (paymentMethod === PaymentMethod.STRIPE)
                    {
                        bus.emit('submit_stripe_payment', res.order);
                    }
                    else
                    {
                        navigate('/order/success/' + res.order.id + '/accesskey/' + res.order.options.access_key , {replace: true});
                    }
                }
                else
                {
                    if (OrderOnlineErrorsMsg[res.error_code])
                    {
                        setCheckoutError(OrderOnlineErrorsMsg[res.error_code]);
                    }
                    else
                    {
                        setCheckoutError("Server error, please try again in a bit.");
                    }
                }
            }
            else
            {
                setCheckoutError("Server error, please try again in a bit.");
            }
        });
    }

    const getCheckoutData = () => {
        let data = {
            cart_token: getCartToken(),
            delivery: {
                type: selectedDeliveryMethod,
            },
            name,
            lastname,
            email,
            phone,
            tip: parseInt(tip * 100),
            payment: {
                type: paymentMethod,
            },
            platform: "web",
        }

        if (selectedDeliveryMethod === DeliveryMethod.DELIVERY)
        {
            data.delivery = {
                type: selectedDeliveryMethod,
                address,
                apartment,
                floor,
                entrance,
                post_code: postCode,
                instructions: "",
                latitude: customerLatitude,
                longitude: customerLongitude,
            }
        }

        return data;
    }

    //EFFECTS
    useEffect(() => {
        loadStripeSettings();
        loadPickupSettings();
        loadEatInSettings();
        loadDeliverySettings();
        loadCashSettings();
    }, []);

    useEffect(() => {
        if (deliverySettings !== null && !deliverySettings.enable)
        {
            setSelectedDeliveryMethod(DeliveryMethod.PICKUP);
        }
    }, [deliverySettings]);

    useEffect(() => {
        if (stripeSettings !== null && stripeSettings.enable)
        {
            getRequest(stripeSettingsEndpoints.get_intent, { cart_token: getCartToken()}, (res) => {
                if (res[0])
                {
                    setStripeClientSecret(res[1].client_secret);
                }
            });
        }
    }, [stripeSettings]);

    useEffect(() => {
        if (stripeSettings !== null && stripeSettings.public_key !== "" && stripeSettings.enable)
        {
            stripePromise.current = loadStripe(stripeSettings.public_key);
        }
    }, [stripeSettings]);

    useEffect(() => {
        if (stripeClientSecret !== "")
        {
            stripeOptions.current = {
                clientSecret: stripeClientSecret,
            };
        } 
    }, [stripeClientSecret]);

    const value = {
        pickupSettings,
        eatInSettings,
        deliverySettings,
        setDeliverySettings,
        stripeSettings,
        loadStripeSettings,
        checkoutStep,
        setCheckoutStep,
        checkoutLoading,
        selectedDeliveryMethod,
        setSelectedDeliveryMethod,
        address,
        setAddress,
        apartment,
        setApartment,
        floor,
        setFloor,
        entrance,
        setEntrance,
        latitude,
        setLatitude,
        longitude,
        setLongitude,
        deliveryAreas,
        deliveryPrice,
        setDeliveryPrice,
        name,
        setName,
        lastname,
        setLastname,
        phone,
        setPhone,
        email,
        setEmail,
        stripePromise,
        stripeOptions,
        stripeClientSecret,
        paymentMethod,
        setPaymentMethod,
        cashSettings,
        tip,
        setTip,
        checkoutError,
        setCheckoutError,
        customerLatitude,
        setCustomerLatitude,
        customerLongitude,
        setCustomerLongitude,
        postCode,
        setPostCode,
        postCheckout,
    }

    return (
        <CheckoutContext.Provider value={value}>
            {children}
        </CheckoutContext.Provider>
    );
}

export const useCheckout = () => useContext(CheckoutContext);
